import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Check, ArrowRight, ArrowLeft, Phone, Lock, Home, Building2, Building, Hotel,
  Calendar, MapPin, PhoneCall, MailCheck, Calculator, Info, HelpCircle, Layers,
  Paintbrush, Droplets, Sparkles, Flame,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// ---------- Types ----------
type DaemmungArt = "hohlraum" | "wdvs" | "innen" | "unsicher";
type Gebaeudetyp = "einfamilienhaus" | "doppelhaushaelfte" | "reihenhaus" | "mehrfamilienhaus";
type Baujahr = "vor1920" | "1921-1950" | "1951-1970" | "1971-1990" | "nach1990";
type Start = "30tage" | "1-3monate" | "3-6monate" | "ueber6monate" | "unsicher";
type AngebotsArt = "beratung" | "angebot" | "preisspanne" | "foerderung";

type SystemId = "eps" | "mineralwolle";
type Thickness = 100 | 120 | 140 | 160 | 180 | 200 | 240;

type ServiceId =
  | "wdvs"
  | "innendaemmung"
  | "energetisch"
  | "malerarbeiten"
  | "badrenovierung"
  | "oberflaechen";

const SERVICES: {
  id: ServiceId;
  title: string;
  desc: string;
  flow: "daemm" | "quick";
  art?: DaemmungArt;
  icon: React.ReactNode;
}[] = [
  {
    id: "wdvs",
    title: "Fassadensanierung & WDVS",
    desc: "Außendämmung mit Wärmedämm-Verbundsystem",
    flow: "daemm",
    art: "wdvs",
    icon: <Layers className="h-6 w-6" />,
  },
  {
    id: "innendaemmung",
    title: "Innendämmung & Schimmelschutz",
    desc: "Calciumsilikat, Sanierputz, Trocknung",
    flow: "daemm",
    art: "innen",
    icon: <Building className="h-6 w-6" />,
  },
  {
    id: "energetisch",
    title: "Energetische Sanierung",
    desc: "KfW- & BAFA-gefördert, ganzheitlich",
    flow: "daemm",
    art: "unsicher",
    icon: <Flame className="h-6 w-6" />,
  },
  {
    id: "malerarbeiten",
    title: "Malerarbeiten & Innensanierung",
    desc: "Wand, Decke, Renovierung",
    flow: "quick",
    icon: <Paintbrush className="h-6 w-6" />,
  },
  {
    id: "badrenovierung",
    title: "Badrenovierung & Fugenlos",
    desc: "Mikrozement, fugenlose Bäder, Komplett",
    flow: "quick",
    icon: <Droplets className="h-6 w-6" />,
  },
  {
    id: "oberflaechen",
    title: "Edle Oberflächen & Böden",
    desc: "Betonoptik, Marmorino, Designböden",
    flow: "quick",
    icon: <Sparkles className="h-6 w-6" />,
  },
];

const SERVICE_LABEL: Record<ServiceId, string> = Object.fromEntries(
  SERVICES.map((s) => [s.id, s.title]),
) as Record<ServiceId, string>;

const THICKNESSES: Thickness[] = [100, 120, 140, 160, 180, 200, 240];

const PRICE_TABLE: Record<SystemId, Record<Thickness, { min: number; avg: number }>> = {
  eps: {
    100: { min: 135, avg: 155 }, 120: { min: 140, avg: 160 }, 140: { min: 145, avg: 165 },
    160: { min: 150, avg: 175 }, 180: { min: 155, avg: 180 }, 200: { min: 160, avg: 185 },
    240: { min: 170, avg: 195 },
  },
  mineralwolle: {
    100: { min: 165, avg: 185 }, 120: { min: 170, avg: 190 }, 140: { min: 175, avg: 195 },
    160: { min: 180, avg: 205 }, 180: { min: 185, avg: 215 }, 200: { min: 190, avg: 225 },
    240: { min: 200, avg: 235 },
  },
};

// ---------- Labels ----------
const ART_LABEL: Record<DaemmungArt, string> = {
  hohlraum: "Hohlraumdämmung", wdvs: "Außendämmung (WDVS)", innen: "Innendämmung", unsicher: "Noch nicht sicher",
};
const GEB_LABEL: Record<Gebaeudetyp, string> = {
  einfamilienhaus: "Einfamilienhaus", doppelhaushaelfte: "Doppelhaushälfte",
  reihenhaus: "Reihenhaus", mehrfamilienhaus: "Mehrfamilienhaus",
};
const BJ_LABEL: Record<Baujahr, string> = {
  vor1920: "Vor 1920", "1921-1950": "1921–1950", "1951-1970": "1951–1970",
  "1971-1990": "1971–1990", nach1990: "Nach 1990",
};
const START_LABEL: Record<Start, string> = {
  "30tage": "In den nächsten 30 Tagen", "1-3monate": "In 1 bis 3 Monaten",
  "3-6monate": "In 3 bis 6 Monaten", ueber6monate: "In über 6 Monaten", unsicher: "Nicht sicher",
};
const ANGEBOT_LABEL: Record<AngebotsArt, string> = {
  beratung: "Persönliche Beratung", angebot: "Detailliertes Angebot",
  preisspanne: "Grobe Preisspanne", foerderung: "Infos zu Förderung",
};

// ---------- Schema ----------
const leadSchema = z.object({
  vorname: z.string().trim().min(2, "Vorname angeben").max(60),
  nachname: z.string().trim().min(2, "Nachname angeben").max(60),
  phone: z.string().trim().min(6, "Bitte gültige Telefonnummer angeben").max(30),
  email: z.string().trim().email("Bitte gültige E-Mail angeben").max(255),
  consent: z.literal(true, { errorMap: () => ({ message: "Bitte Datenschutz bestätigen" }) }),
});

// ---------- Small UI helpers ----------
const StepHeader = ({ kicker, title, sub }: { kicker: string; title: string; sub?: string }) => (
  <div className="mb-8">
    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">{kicker}</p>
    <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-3">{title}</h2>
    {sub && <p className="text-muted-foreground text-sm md:text-base">{sub}</p>}
  </div>
);

const ChoiceCard = ({
  active, onClick, icon, title, desc,
}: { active: boolean; onClick: () => void; icon?: React.ReactNode; title: string; desc?: string }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left w-full p-5 rounded-2xl border-2 transition-all ${
      active ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"
    }`}
  >
    {icon && <div className="mb-3 text-foreground">{icon}</div>}
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium">{title}</span>
      {active && <Check className="h-4 w-4 shrink-0" />}
    </div>
    {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
  </button>
);

const ListChoice = ({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full px-5 py-4 rounded-xl border-2 font-medium text-sm transition-all ${
      active ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/30"
    }`}
  >
    {label}
  </button>
);

// ---------- Page ----------
const AngebotFassade = () => {
  const [step, setStep] = useState(1);

  // Service (first choice)
  const [service, setService] = useState<ServiceId | null>(null);

  // Wizard answers
  const [art, setArt] = useState<DaemmungArt | null>(null);
  const [gebaeude, setGebaeude] = useState<Gebaeudetyp | null>(null);
  const [baujahr, setBaujahr] = useState<Baujahr | null>(null);
  const [start, setStart] = useState<Start | null>(null);
  const [plz, setPlz] = useState("");
  const [angebotsArt, setAngebotsArt] = useState<AngebotsArt | null>(null);

  // Optional WDVS price details (only if preisspanne + wdvs)
  const [area, setArea] = useState<number>(200);
  const [system, setSystem] = useState<SystemId>("eps");
  const [thickness, setThickness] = useState<Thickness>(160);

  // Kontakt
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ min: number; avg: number } | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const showPriceStep = angebotsArt === "preisspanne" && (art === "wdvs" || art === "unsicher");

  const flow: "daemm" | "quick" =
    service ? SERVICES.find((s) => s.id === service)!.flow : "daemm";
  const TOTAL_STEPS = flow === "quick" ? 2 : 7;

  // Quick flow uses logical steps [1=service, 7=contact]; daemm uses 1..7.
  const displayStep = flow === "quick" && step === 7 ? 2 : step;

  const next = () => {
    setStep((s) => {
      if (flow === "quick" && s === 1) return 7; // skip to contact in quick flow
      return s + 1;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => {
      if (flow === "quick" && s === 7) return 1;
      return Math.max(1, s - 1);
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleServiceSelect = (id: ServiceId) => {
    const svc = SERVICES.find((s) => s.id === id)!;
    setService(id);
    if (svc.art) setArt(svc.art);
    else setArt(null);
  };

  const formatPrice = (n: number) => new Intl.NumberFormat("de-DE").format(n) + " €";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = leadSchema.safeParse({ vorname, nachname, phone, email, consent });
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      parsed.error.errors.forEach((err) => { errs[err.path[0] as string] = err.message; });
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);

    let priceMin = 0, priceAvg = 0;
    if (showPriceStep) {
      const unit = PRICE_TABLE[system][thickness];
      priceMin = Math.round((area * unit.min) / 100) * 100;
      priceAvg = Math.round((area * unit.avg) / 100) * 100;
    }

    const notesObj = {
      service: service ? SERVICE_LABEL[service] : null,
      flow,
      daemmungsart: art ? ART_LABEL[art] : null,
      gebaeudetyp: gebaeude ? GEB_LABEL[gebaeude] : null,
      baujahr: baujahr ? BJ_LABEL[baujahr] : null,
      start: start ? START_LABEL[start] : null,
      plz,
      angebotsart: angebotsArt ? ANGEBOT_LABEL[angebotsArt] : null,
      ...(showPriceStep ? { wdvs_system: system, wdvs_thickness_mm: thickness, wdvs_area_m2: area } : {}),
    };

    const { error } = await supabase.from("facade_leads").insert({
      name: `${parsed.data.vorname} ${parsed.data.nachname}`.trim(),
      phone: parsed.data.phone,
      email: parsed.data.email,
      area_m2: showPriceStep ? area : 0,
      house_type: gebaeude ?? (service ?? "n/a"),
      stories: showPriceStep ? thickness : 0,
      estimated_price_min: priceMin,
      estimated_price_max: priceAvg,
      notes: JSON.stringify(notesObj, null, 2),
    });

    if (error) {
      setSubmitting(false);
      toast({ title: "Etwas ist schiefgelaufen", description: "Bitte später erneut versuchen oder uns direkt anrufen.", variant: "destructive" });
      return;
    }

    // E-Mail-Benachrichtigung an info@ per SMTP
    const detailEntries: Record<string, string> = {};
    if (notesObj.daemmungsart) detailEntries["Dämmungsart"] = notesObj.daemmungsart;
    if (notesObj.gebaeudetyp) detailEntries["Gebäudetyp"] = notesObj.gebaeudetyp;
    if (notesObj.baujahr) detailEntries["Baujahr"] = notesObj.baujahr;
    if (notesObj.start) detailEntries["Startzeitpunkt"] = notesObj.start;
    if (notesObj.plz) detailEntries["PLZ"] = notesObj.plz;
    if (notesObj.angebotsart) detailEntries["Gewünscht"] = notesObj.angebotsart;
    if (showPriceStep) {
      detailEntries["WDVS-System"] = system === "eps" ? "EPS (Styropor)" : "Mineralwolle";
      detailEntries["Dämmstärke"] = `${thickness} mm`;
      detailEntries["Fassadenfläche"] = `${area} m²`;
    }

    await supabase.functions.invoke("send-contact-email", {
      body: {
        source: "angebotsrechner",
        name: `${parsed.data.vorname} ${parsed.data.nachname}`.trim(),
        email: parsed.data.email,
        phone: parsed.data.phone,
        service: service ? SERVICE_LABEL[service] : undefined,
        details: detailEntries,
        estimate: showPriceStep ? { min: priceMin, avg: priceAvg } : null,
      },
    });

    setSubmitting(false);

    if (showPriceStep) setDone({ min: priceMin, avg: priceAvg });
    setSubmitted(true);
    setStep(99); // success screen
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Step validation
  const canNext = (() => {
    switch (step) {
      case 1: return !!service;
      case 2: return !!gebaeude;
      case 3: return !!baujahr;
      case 4: return !!start;
      case 5: return /^\d{5}$/.test(plz);
      case 6: return !!angebotsArt;
      case 7: return showPriceStep ? area >= 20 : true;
      default: return true;
    }
  })();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Angebot Fassadendämmung in Herford: In 60 Sek. anfragen"
        description="Angebotsrechner Fassadendämmung in Herford: WDVS, Hohlraum- oder Innendämmung in wenigen Schritten konfigurieren. Unverbindlich, schnell, kostenlos."
        keywords="Angebot Fassadendämmung Herford, Angebotsrechner WDVS Herford, Kosten Fassadendämmung, Dämmung Angebot Herford, Fassaden-Check Herford"
        canonical="https://malerfachbetrieb-stein.de/angebot-fassadensanierung"
      />
      <Navigation />
      <WhatsAppButton />

      <main className="pt-32 pb-24">
        <div className="container mx-auto max-w-3xl">
          {/* Header */}
          <div className="text-center mb-10">
            <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">Angebotsrechner</p>
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-4 leading-tight">
              Ihr persönliches<br />Angebot
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              In wenigen Schritten zur passenden Lösung – kostenlos, unverbindlich & ohne Verpflichtung.
            </p>
          </div>

          {/* Progress */}
          {!submitted && (
            <div className="flex items-center gap-1.5 mb-10">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div key={i} className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${
                  displayStep > i ? "bg-[#22c55e]" : "bg-foreground/10"
                }`} />
              ))}
            </div>
          )}

          <div className="bg-card border border-border rounded-2xl p-6 md:p-10 animate-fade-in">
            {/* STEP 1 – Leistung wählen */}
            {step === 1 && (
              <>
                <StepHeader
                  kicker={`Schritt 1 von ${TOTAL_STEPS}`}
                  title="Welche Leistung interessiert Sie?"
                  sub="Wählen Sie aus unseren Bereichen – wir leiten Sie zum passenden Anfrageweg."
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SERVICES.map((s) => (
                    <ChoiceCard
                      key={s.id}
                      active={service === s.id}
                      onClick={() => handleServiceSelect(s.id)}
                      icon={s.icon}
                      title={s.title}
                      desc={s.desc}
                    />
                  ))}
                </div>
                {service && (
                  <p className="mt-5 text-xs text-muted-foreground">
                    {flow === "daemm"
                      ? "→ Sie durchlaufen den kurzen Dämm-Konfigurator (7 Schritte) für ein passgenaues Angebot."
                      : "→ Hinterlassen Sie im nächsten Schritt Ihre Kontaktdaten – wir melden uns innerhalb von 24 h."}
                  </p>
                )}
              </>
            )}

            {/* STEP 2 – Gebäudetyp */}
            {step === 2 && flow === "daemm" && (
              <>
                <StepHeader kicker={`Schritt 2 von ${TOTAL_STEPS}`} title="Um welchen Gebäudetyp handelt es sich?" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ChoiceCard active={gebaeude === "einfamilienhaus"} onClick={() => setGebaeude("einfamilienhaus")}
                    icon={<Home className="h-6 w-6" />} title="Einfamilienhaus" />
                  <ChoiceCard active={gebaeude === "doppelhaushaelfte"} onClick={() => setGebaeude("doppelhaushaelfte")}
                    icon={<Building2 className="h-6 w-6" />} title="Doppelhaushälfte" />
                  <ChoiceCard active={gebaeude === "reihenhaus"} onClick={() => setGebaeude("reihenhaus")}
                    icon={<Hotel className="h-6 w-6" />} title="Reihenhaus" />
                  <ChoiceCard active={gebaeude === "mehrfamilienhaus"} onClick={() => setGebaeude("mehrfamilienhaus")}
                    icon={<Building className="h-6 w-6" />} title="Mehrfamilienhaus" />
                </div>
              </>
            )}

            {/* STEP 3 – Baujahr */}
            {step === 3 && flow === "daemm" && (
              <>
                <StepHeader kicker={`Schritt 3 von ${TOTAL_STEPS}`} title="In welchem Baujahr wurde das Haus errichtet?" />
                <div className="grid grid-cols-1 gap-3">
                  {(Object.keys(BJ_LABEL) as Baujahr[]).map((b) => (
                    <ListChoice key={b} active={baujahr === b} onClick={() => setBaujahr(b)} label={BJ_LABEL[b]} />
                  ))}
                </div>
              </>
            )}

            {/* STEP 4 – Startzeitpunkt */}
            {step === 4 && flow === "daemm" && (
              <>
                <StepHeader kicker={`Schritt 4 von ${TOTAL_STEPS}`} title="Wann möchten Sie mit der Dämmung starten?" />
                <div className="grid grid-cols-1 gap-3">
                  {(Object.keys(START_LABEL) as Start[]).map((s) => (
                    <ListChoice key={s} active={start === s} onClick={() => setStart(s)} label={START_LABEL[s]} />
                  ))}
                </div>
              </>
            )}

            {/* STEP 5 – PLZ */}
            {step === 5 && flow === "daemm" && (
              <>
                <StepHeader kicker={`Schritt 5 von ${TOTAL_STEPS}`} title="In welcher Postleitzahl befindet sich das Haus?" />
                <Label htmlFor="plz" className="text-sm text-muted-foreground mb-2 block">Postleitzahl Ihres Gebäudes</Label>
                <div className="relative max-w-sm">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="plz" inputMode="numeric" maxLength={5}
                    value={plz} onChange={(e) => setPlz(e.target.value.replace(/\D/g, "").slice(0, 5))}
                    placeholder="z. B. 32051" className="h-14 rounded-xl text-lg pl-11"
                  />
                </div>
              </>
            )}

            {/* STEP 6 – Wie Angebot erhalten */}
            {step === 6 && flow === "daemm" && (
              <>
                <StepHeader
                  kicker={`Schritt 6 von ${TOTAL_STEPS}`}
                  title="Wie möchten Sie Ihr Angebot erhalten?"
                  sub="Keine Verpflichtung – wir schicken Ihnen die Infos auf dem Weg, der am besten passt."
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ChoiceCard active={angebotsArt === "beratung"} onClick={() => setAngebotsArt("beratung")}
                    icon={<PhoneCall className="h-6 w-6" />} title="Persönliche Beratung"
                    desc="Direkt mit einem Experten sprechen" />
                  <ChoiceCard active={angebotsArt === "angebot"} onClick={() => setAngebotsArt("angebot")}
                    icon={<MailCheck className="h-6 w-6" />} title="Detailliertes Angebot"
                    desc="Schriftliches Angebot in Ihrem Postfach" />
                  <ChoiceCard active={angebotsArt === "preisspanne"} onClick={() => setAngebotsArt("preisspanne")}
                    icon={<Calculator className="h-6 w-6" />} title="Grobe Preisspanne"
                    desc="Schnelle Orientierung ohne Wartezeit" />
                  <ChoiceCard active={angebotsArt === "foerderung"} onClick={() => setAngebotsArt("foerderung")}
                    icon={<Info className="h-6 w-6" />} title="Infos zu Förderung"
                    desc="Welche Zuschüsse stehen Ihnen zu?" />
                </div>
              </>
            )}

            {/* STEP 7 – Kontakt (+ ggf. WDVS-Detail) */}
            {step === 7 && (
              <form onSubmit={handleSubmit}>
                <StepHeader
                  kicker={`Schritt ${displayStep} von ${TOTAL_STEPS}`}
                  title={flow === "quick" ? "Ihre Kontaktdaten" : "Wohin sollen wir Ihr Angebot schicken?"}
                  sub={flow === "quick"
                    ? `Anfrage für „${service ? SERVICE_LABEL[service] : ""}" – wir melden uns innerhalb von 24 h persönlich.`
                    : "Kostenlos und unverbindlich – wir melden uns innerhalb von 24 h."}
                />

                {/* Zusammenfassung */}
                <div className="mb-8 p-5 rounded-2xl bg-muted/40">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Ihre Angaben</p>
                  <ul className="space-y-1.5 text-sm">
                    {service && <li className="flex gap-2"><Check className="h-4 w-4 text-[#22c55e] shrink-0 mt-0.5" /><span>{SERVICE_LABEL[service]}</span></li>}
                    {flow === "daemm" && art && <li className="flex gap-2"><Home className="h-4 w-4 text-[#22c55e] shrink-0 mt-0.5" /><span>{ART_LABEL[art]}{gebaeude ? ` · ${GEB_LABEL[gebaeude]}` : ""}</span></li>}
                    {flow === "daemm" && start && <li className="flex gap-2"><Calendar className="h-4 w-4 text-[#22c55e] shrink-0 mt-0.5" /><span>{START_LABEL[start]}</span></li>}
                    {flow === "daemm" && plz && <li className="flex gap-2"><MapPin className="h-4 w-4 text-[#22c55e] shrink-0 mt-0.5" /><span>PLZ {plz}</span></li>}
                    {flow === "daemm" && angebotsArt && <li className="flex gap-2"><Calculator className="h-4 w-4 text-[#22c55e] shrink-0 mt-0.5" /><span>{ANGEBOT_LABEL[angebotsArt]}</span></li>}
                  </ul>
                </div>

                {/* Optionale WDVS-Daten für Preisspanne */}
                {showPriceStep && (
                  <div className="mb-8 space-y-6">
                    <div>
                      <Label htmlFor="area" className="text-sm font-medium mb-2 block">Fassadenfläche (m²)</Label>
                      <div className="relative">
                        <Input id="area" type="number" inputMode="numeric" min={20} max={5000}
                          value={area} onChange={(e) => setArea(Math.max(0, parseInt(e.target.value || "0", 10)))}
                          className="h-12 rounded-xl pr-14" />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">m²</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-3 block">Dämmsystem</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <ListChoice active={system === "eps"} onClick={() => setSystem("eps")} label="EPS (Styropor)" />
                        <ListChoice active={system === "mineralwolle"} onClick={() => setSystem("mineralwolle")} label="Mineralwolle" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-3 block">Dämmstärke</Label>
                      <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                        {THICKNESSES.map((t) => (
                          <button key={t} type="button" onClick={() => setThickness(t)}
                            className={`py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                              thickness === t ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/30"
                            }`}>{t} mm</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4 mb-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="vorname" className="text-sm">Vorname *</Label>
                      <Input id="vorname" value={vorname} onChange={(e) => setVorname(e.target.value)} className="mt-2 h-12 rounded-xl" maxLength={60} />
                      {errors.vorname && <p className="text-xs text-destructive mt-1">{errors.vorname}</p>}
                    </div>
                    <div>
                      <Label htmlFor="nachname" className="text-sm">Nachname *</Label>
                      <Input id="nachname" value={nachname} onChange={(e) => setNachname(e.target.value)} className="mt-2 h-12 rounded-xl" maxLength={60} />
                      {errors.nachname && <p className="text-xs text-destructive mt-1">{errors.nachname}</p>}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm">E-Mail-Adresse *</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 rounded-xl" maxLength={255} />
                    {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm">Telefonnummer *</Label>
                    <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-2 h-12 rounded-xl" maxLength={30} />
                    {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
                  </div>

                  <label className="flex items-start gap-3 text-xs text-muted-foreground cursor-pointer p-4 rounded-xl bg-muted/40">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border" />
                    <span>
                      Ich stimme der <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> zu. Ihre Daten werden nur zur Angebotserstellung genutzt und <strong>nicht an Dritte weitergegeben</strong>.
                    </span>
                  </label>
                  {errors.consent && <p className="text-xs text-destructive">{errors.consent}</p>}
                </div>

                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
                  <Lock className="h-3.5 w-3.5" />
                  <span>SSL-verschlüsselt · Keine Weitergabe an Dritte</span>
                </div>

                <Button type="submit" disabled={submitting} className="w-full h-14 rounded-xl text-sm tracking-[0.15em] uppercase">
                  {submitting ? "Wird gesendet…" : "Jetzt Anfrage absenden"} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <div className="text-center mt-5">
                  <a
                    href="https://wa.me/491788783369?text=Hallo%2C%20ich%20habe%20gerade%20den%20Angebotsrechner%20genutzt."
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-[#22c55e] hover:underline font-medium"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Lieber per WhatsApp? Direkt schreiben
                  </a>
                </div>
              </form>
            )}

            {/* SUCCESS – Step 8 */}
            {submitted && step > TOTAL_STEPS && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-foreground text-background mb-6">
                  <Check className="h-6 w-6" />
                </div>

                {done ? (
                  <>
                    <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Ihre Preisschätzung</p>
                    <h2 className="font-serif text-3xl md:text-5xl mb-2">{formatPrice(done.min)} – {formatPrice(done.avg)}</h2>
                    <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
                      Spanne von Ab-Preis bis Durchschnittspreis für komplette Fassadendämmung (WDVS) inkl. Material und Arbeit. Das verbindliche Angebot erstellen wir nach dem kostenlosen Vor-Ort-Termin.
                    </p>
                    <p className="text-[11px] text-muted-foreground mb-8 max-w-xl mx-auto leading-relaxed">
                      Hinweis: Die Berechnung dient ausschließlich der ersten Orientierung. Der tatsächliche Preis hängt von Gebäudehöhe, Untergrund, Fensteranschlüssen, Sockelausbildung, Gerüstaufwand und weiteren objektspezifischen Faktoren ab.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="font-serif text-3xl md:text-4xl mb-4">Vielen Dank!</h2>
                    <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                      Wir haben Ihre Anfrage erhalten und melden uns innerhalb von <strong>24 Stunden</strong> persönlich bei Ihnen.
                    </p>
                  </>
                )}

                <div className="bg-foreground text-background rounded-2xl p-6 mb-4">
                  <p className="text-sm mb-4">Sie möchten direkt sprechen?</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <a href="tel:+4952213832130" className="inline-flex items-center gap-2 font-medium text-lg">
                      <Phone className="h-5 w-5" /> 05221 / 3832130
                    </a>
                    <span className="hidden sm:inline text-background/30">|</span>
                    <a
                      href="https://wa.me/491788783369?text=Hallo%2C%20ich%20habe%20gerade%20den%20Angebotsrechner%20genutzt%20und%20m%C3%B6chte%20mein%20Angebot%20besprechen."
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 font-medium text-lg text-[#4ade80] hover:text-[#86efac] transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </a>
                  </div>
                </div>

                <Link to="/" className="text-xs text-muted-foreground underline">Zurück zur Startseite</Link>
              </div>
            )}

            {/* Navigation */}
            {!submitted && (
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                {step > 1 && step !== 7 && (
                  <Button type="button" variant="outline" onClick={back} className="rounded-xl h-12 sm:w-32">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
                  </Button>
                )}
                {step < 7 && (
                  <Button type="button" onClick={next} disabled={!canNext}
                    className="rounded-xl h-12 flex-1 text-sm tracking-[0.15em] uppercase">
                    Weiter <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
                {step === 7 && (
                  <Button type="button" variant="outline" onClick={back} className="rounded-xl h-12 sm:w-32">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AngebotFassade;
